'use client'

/**
 * Interactive "what do my olives buy" calculator for the pricing page.
 *
 * Pick a model (and clip length for per-second video models) and see the cost
 * per generation plus how many generations each plan's monthly olives cover.
 * Reads the same marketing-config pricing data as the per-model tables, so it
 * can never drift from the published rates.
 */

import { useMemo, useState } from 'react'
import { useExtracted } from 'next-intl'
import { OliveIcon } from '@/components/icons/OliveIcon'
import { trackPricingCalculatorInteracted } from '@/lib/analytics'
import {
  getModelPricingVariants,
  usdToOlives,
  type AIModel,
  type ModelCategory,
  type PricingVariant,
} from '@/lib/marketing-config'

// Monthly olive allocations mirrored from PlanCards (Pro $30 -> 300, Studio $150 -> 1,500).
const PLAN_ALLOCATIONS = [
  { key: 'pro', name: 'Pro', olives: 300 },
  { key: 'studio', name: 'Studio', olives: 1500 },
] as const

// $10 top-up = 100 olives — available on every plan, including Free.
const TOPUP_OLIVES = 100
const TOPUP_USD = 10

const CLIP_LENGTHS = [4, 5, 8, 10, 12] as const

type Unit = 'clip' | 'image' | 'set'

interface CalculatorEntry {
  group: string
  model: AIModel
  unit: Unit
}

// Video + still models only — Sets include a $0 screenshot pseudo-model that
// turns the math into nonsense; the rate tables below cover them.
function buildEntries(videoGroups: ModelCategory[], stillModels: AIModel[]): CalculatorEntry[] {
  return [
    ...videoGroups.flatMap(group =>
      group.models.map(model => ({ group: group.category, model, unit: 'clip' as Unit })),
    ),
    ...stillModels.map(model => ({ group: 'Still Generation / Editing', model, unit: 'image' as Unit })),
  ]
}

export interface PricingCalculatorProps {
  /**
   * The roster as the page decided it at render time (sunset filter applied
   * server-side). Taking it as props rather than reading the catalog here is
   * what keeps the hydrated options identical to the server markup.
   */
  videoGroups: ModelCategory[]
  stillModels: AIModel[]
}

const formatCount = (n: number) => Math.floor(n).toLocaleString('en-US')
const formatOlives = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: n < 1 ? 2 : 1 }).format(n)
// Money always shows cents ("$0.80", never "$0.8"); sub-dime values get a
// third digit so cheap models don't round to a misleading zero.
const formatUsd = (n: number) =>
  `$${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: n < 0.1 ? 0 : 2,
    maximumFractionDigits: n < 0.1 ? 3 : 2,
  }).format(n)}`

export function PricingCalculator({ videoGroups, stillModels }: PricingCalculatorProps) {
  const t = useExtracted()
  const entries = useMemo(() => buildEntries(videoGroups, stillModels), [videoGroups, stillModels])

  const [modelKey, setModelKey] = useState('kling-v3')
  const [variantIdx, setVariantIdx] = useState(0)
  const [seconds, setSeconds] = useState<number>(8)

  const entry = entries.find(e => e.model.key === modelKey) ?? entries[0]
  const variants: PricingVariant[] = getModelPricingVariants(entry.model.key) ?? [
    { label: 'Base', priceUsd: entry.model.priceUsd } as PricingVariant,
  ]
  const variant = variants[Math.min(variantIdx, variants.length - 1)]
  const isPerSecond = entry.unit === 'clip'

  const usdPerUnit = variant.priceUsd * (isPerSecond ? seconds : 1)
  const olivesPerUnit = usdToOlives(usdPerUnit)

  const unitLabel = (raw: number): string => {
    const count = formatCount(raw)
    switch (entry.unit) {
      case 'clip':
        return t('{count} clips', { count })
      case 'image':
        return t('{count} images', { count })
      case 'set':
        return t('{count} sets', { count })
    }
  }

  // [color-scheme:dark] keeps the native option popup dark — without it the
  // browser can render a light popup behind our white option text. The
  // explicit option/optgroup backgrounds cover engines that paint popup rows
  // from author CSS instead (Windows Chrome/Firefox).
  const selectClasses =
    'w-full appearance-none rounded-lg border border-white/10 bg-white/[0.05] px-3 py-2 text-sm text-white/90 outline-none transition-colors [color-scheme:dark] [&_optgroup]:bg-neutral-900 [&_option]:bg-neutral-900 hover:border-white/20 focus:border-white/30'

  // Rendered embedded inside the "Meet the Olive" section — no chrome of its
  // own; the host section provides heading and intro.
  return (
    <div>
      <div className="grid gap-4 sm:grid-cols-2">
        {/* Model picker */}
        <label className="block">
          <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.15em] text-white/40">
            {t('Model')}
          </span>
          <select
            className={selectClasses}
            value={entry.model.key}
            onChange={e => {
              setModelKey(e.target.value)
              setVariantIdx(0)
              const next = entries.find(en => en.model.key === e.target.value)
              trackPricingCalculatorInteracted({
                modelKey: e.target.value,
                variantLabel: (getModelPricingVariants(e.target.value) ?? [])[0]?.label ?? 'Base',
                seconds: next?.unit === 'clip' ? seconds : undefined,
                unit: next?.unit ?? 'clip',
              })
            }}
          >
            {[...new Set(entries.map(e => e.group))].map(group => (
              <optgroup key={group} label={group}>
                {entries
                  .filter(e => e.group === group)
                  .map(e => (
                    <option key={e.model.key} value={e.model.key}>
                      {e.model.name}
                    </option>
                  ))}
              </optgroup>
            ))}
          </select>
        </label>

        {/* Variant picker (only when the model has tiers) */}
        {variants.length > 1 && (
          <label className="block">
            <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.15em] text-white/40">
              {t('Settings')}
            </span>
            <select
              className={selectClasses}
              value={variantIdx}
              onChange={e => {
                setVariantIdx(Number(e.target.value))
                trackPricingCalculatorInteracted({
                  modelKey: entry.model.key,
                  variantLabel: variants[Number(e.target.value)]?.label ?? 'Base',
                  seconds: isPerSecond ? seconds : undefined,
                  unit: entry.unit,
                })
              }}
            >
              {variants.map((v, idx) => (
                <option key={v.label} value={idx}>
                  {v.label}
                </option>
              ))}
            </select>
          </label>
        )}

        {/* Clip length (per-second models only) */}
        {isPerSecond && (
          <div className="sm:col-span-2">
            <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.15em] text-white/40">
              {t('Clip length')}
            </span>
            <div className="flex flex-wrap gap-2">
              {CLIP_LENGTHS.map(len => (
                <button
                  key={len}
                  type="button"
                  onClick={() => {
                    setSeconds(len)
                    trackPricingCalculatorInteracted({
                      modelKey: entry.model.key,
                      variantLabel: variant.label,
                      seconds: len,
                      unit: entry.unit,
                    })
                  }}
                  className={`rounded-lg border px-3.5 py-1.5 text-sm tabular-nums transition-colors ${
                    seconds === len
                      ? 'border-white/40 bg-white/10 text-white'
                      : 'border-white/10 bg-white/[0.03] text-white/60 hover:border-white/20 hover:text-white/80'
                  }`}
                >
                  {len}s
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* The answer as a sentence — the reader should never have to assemble
            "8 🫒 $0.8 per clip" into meaning themselves. */}
      <div className="mt-6 border-t border-white/10 pt-6">
        <p className="text-base text-white/90">
          {entry.unit === 'clip' &&
            t.rich('One {seconds}-second clip costs <b>{olives} olives</b> ({usd})', {
              seconds: String(seconds),
              olives: formatOlives(olivesPerUnit),
              usd: formatUsd(usdPerUnit),
              b: chunks => <span className="font-semibold text-white">{chunks}</span>,
            })}
          {entry.unit === 'image' &&
            t.rich('One image costs <b>{olives} olives</b> ({usd})', {
              olives: formatOlives(olivesPerUnit),
              usd: formatUsd(usdPerUnit),
              b: chunks => <span className="font-semibold text-white">{chunks}</span>,
            })}
          {entry.unit === 'set' &&
            t.rich('One set costs <b>{olives} olives</b> ({usd})', {
              olives: formatOlives(olivesPerUnit),
              usd: formatUsd(usdPerUnit),
              b: chunks => <span className="font-semibold text-white">{chunks}</span>,
            })}
        </p>
      </div>

      {/* What each plan's monthly olives cover */}
      <div className="mt-4 divide-y divide-white/10">
        {PLAN_ALLOCATIONS.map(plan => (
          <div key={plan.key} className="flex items-baseline justify-between gap-4 py-3">
            <span className="text-sm text-white/80">
              {plan.name}
              <span className="ms-2 inline-flex items-center gap-1 text-white/40">
                {plan.olives.toLocaleString('en-US')}
                <OliveIcon className="h-3 w-3 translate-y-px" />
                {t('/mo')}
              </span>
            </span>
            <span className="whitespace-nowrap font-medium tabular-nums text-white">
              {olivesPerUnit > 0 ? `≈ ${unitLabel(plan.olives / olivesPerUnit)}` : '—'}
              <span className="ms-1 text-sm font-normal text-white/40">{t('every month')}</span>
            </span>
          </div>
        ))}
      </div>

      {/* Top-ups are a different kind of thing — one sentence, not a row. */}
      <p className="mt-4 text-sm text-white/60">
        {olivesPerUnit > 0
          ? t.rich('Need more? Every {price} top-up adds {olives} olives — about <b>{units} more</b>, on any plan.', {
              price: `$${TOPUP_USD}`,
              olives: String(TOPUP_OLIVES),
              units: unitLabel(TOPUP_OLIVES / olivesPerUnit),
              b: chunks => <span className="font-medium text-white/90">{chunks}</span>,
            })
          : t('Top-ups work on every plan: {price} adds {olives} olives.', {
              price: `$${TOPUP_USD}`,
              olives: String(TOPUP_OLIVES),
            })}
      </p>

      <p className="mt-3 text-xs text-white/35">
        {t('Estimates at published rates. Unused olives roll over for 12 months.')}
      </p>
    </div>
  )
}
